import { useSuspenseQueries } from "@tanstack/react-query";
import { isLeadership, type ClinicUser } from "@users/userClient";
import {
  viewsClient,
  type Audience,
  type ViewAttention,
} from "@views/viewsClient";
import { viewsKeys } from "@views/viewsQueries";
import type { VisitDetail } from "@visits/visitsClient";
import { useVisitDetail } from "@visits/visitsQueries";

/** Which of a visit's entities a surfacing belongs to. */
export interface AttentionScope {
  entityId: string;
  kind: "visit" | "patient" | "stage";
  /** The stage's name; absent for the visit and patient scopes. */
  name?: string;
}

/**
 * One row of the visit-level attention report: a surfaced view's audience plus
 * the entity it was surfaced on.
 *
 * The same view can appear twice — surfaced on the patient and again on a stage
 * is two surfacings with two latencies, so rows are keyed by scope, not deduped.
 */
export interface AttentionRow extends ViewAttention {
  scope: AttentionScope;
}

/** Whether an audience names someone in particular, vs the generic "everyone". */
export const isTargeted = (audience: Audience): boolean =>
  audience.userId !== null || audience.systemRole !== null;

/**
 * Why a row reads the way it does (BE-2151). Only `audienceMissed` and
 * `nobodyServed` say anything about staff; `notEligible` is a visibility fact
 * about RLS, and `delivered` is the happy path.
 *
 * Keeping these apart is the entire point of the audience fields: an empty
 * `viewers` on its own accuses exactly the people the policy excluded.
 */
export type AttentionOutcome =
  | "delivered"
  | "notEligible"
  | "audienceMissed"
  | "nobodyServed";

/**
 * Whether RLS left nobody who could have been served this row.
 *
 * `leadershipOnly` hides a view from non-leadership staff — it does not mean
 * nobody was eligible, because leadership still is. So this is only true when
 * every audience names a role the flag hides it from: a leadership-only view
 * aimed solely at DVMs reaches nobody, while one aimed at "everyone" reaches
 * leadership, and a margin review no manager ever opened is a real finding.
 *
 * Whether a role scope is actually hidden is a fact about the clinic, not the row:
 * a user holds several roles (BE-2279), so a DVM who is also a practice manager
 * matches the DVM audience *and* passes the leadership filter. Hence `roster` —
 * only a role nobody holds alongside leadership excuses the row. The roster answers
 * a user-scoped audience the same way: a named person who isn't leadership was
 * never eligible for a leadership-only view.
 *
 * Both remaining unknowns resolve toward keeping a possible miss rather than
 * excusing one: a userId nobody on the roster matches counts as eligible, and an
 * empty or unfetched roster excuses nothing at all. The second is why callers
 * disclose an absent roster instead of presenting the counts as settled, via
 * {@link rosterDecides}.
 */
const hiddenFromEveryAudience = (
  row: AttentionRow,
  roster: ClinicUser[],
): boolean =>
  row.leadershipOnly &&
  row.audiences.length > 0 &&
  roster.length > 0 &&
  row.audiences.every((audience) => {
    const scope = audience.systemRole;
    if (scope === null) return hiddenFromNamedUser(audience.userId, roster);
    if (scope === "LEADERSHIP") return false;
    return !roster.some(
      (user) =>
        isLeadership(user) && user.roles.some((r) => r.systemRole === scope),
    );
  });

/**
 * Whether a user-scoped audience names someone RLS hides a leadership-only view
 * from. A userId the roster doesn't carry is not hidden, and neither is the generic
 * "everyone" (a null userId): both keep the miss.
 */
const hiddenFromNamedUser = (
  userId: string | null,
  roster: ClinicUser[],
): boolean => {
  const named = roster.find((user) => user.id === userId);
  return named !== undefined && !isLeadership(named);
};

/**
 * Whether the roster is what decides this row: a leadership-only surfacing nobody
 * was served, aimed at an audience the roster can actually answer. Without a roster
 * every one of these reads as a staff finding, so a caller holding any must say the
 * classification is degraded rather than let the headline read as settled.
 *
 * "Aimed at someone" is not enough: `hiddenFromEveryAudience` reads a generic or
 * LEADERSHIP-scoped audience the same way with or without a roster, so only a
 * non-leadership role scope or a named user leaves it anything to decide.
 */
export const rosterDecides = (row: AttentionRow): boolean =>
  row.leadershipOnly &&
  row.viewers.length === 0 &&
  row.audiences.some(
    (audience) =>
      (audience.systemRole !== null && audience.systemRole !== "LEADERSHIP") ||
      audience.userId !== null,
  );

/** `roster` is every clinic user with their held roles — see `hiddenFromEveryAudience`. */
export const attentionOutcome = (
  row: AttentionRow,
  roster: ClinicUser[],
): AttentionOutcome => {
  if (row.viewers.length > 0) return "delivered";
  if (hiddenFromEveryAudience(row, roster)) return "notEligible";
  return row.audiences.some(isTargeted) ? "audienceMissed" : "nobodyServed";
};

/** Rows that assert someone wasn't shown something they were meant to be. */
export const isStaffFinding = (
  row: AttentionRow,
  roster: ClinicUser[],
): boolean =>
  attentionOutcome(row, roster) === "audienceMissed" ||
  attentionOutcome(row, roster) === "nobodyServed";

export interface AttentionReport {
  rows: AttentionRow[];
  /**
   * Patient-scope surfacings falling outside this visit's window, left out of
   * `rows`. Non-zero means the patient carries attention history — from earlier
   * visits, or from later ones when grading a finished visit — that this visit
   * is deliberately not graded on.
   */
  outsideVisit: number;
  /**
   * True when patient-scope rows were counted with no lower bound to hold them
   * out by — a visit that is neither scheduled nor has entered a stage, e.g. a
   * walk-in. The patient's whole history is then in `rows` and `outsideVisit` is
   * 0, which reads identically to a patient with no history. Callers must say so
   * on screen; the alternative, dropping the rows, hides real surfacings just as
   * silently.
   */
  unboundedHistory: boolean;
}

/**
 * Every entity whose surfaced slot feeds a visit's picture: the visit, its
 * patient, and each stage. The report is scoped to the entity asked about
 * (BE-2151), so a combined picture is one request per entity.
 */
export const visitScopes = (visit: VisitDetail): AttentionScope[] => [
  { entityId: visit.id, kind: "visit" },
  { entityId: visit.patientId, kind: "patient" },
  ...visit.stages.map((stage) => ({
    entityId: stage.id,
    kind: "stage" as const,
    name: stage.name,
  })),
];

/**
 * When this visit's window opens — its scheduled start, or the earliest stage it
 * has actually entered. Null for a visit that is neither scheduled nor started,
 * which offers nothing to bound against.
 */
export const visitWindowStart = (visit: VisitDetail): number | null => {
  const times = [visit.scheduledStart, ...visit.stages.map((s) => s.enteredAt)]
    .filter((at): at is string => at !== null)
    .map(Date.parse);
  return times.length > 0 ? Math.min(...times) : null;
};

/**
 * When this visit's window closes — its scheduled end, or the last stage it
 * completed. Null while the visit is still underway: an open visit has no upper
 * edge, and inventing one would drop the surfacings being made right now.
 *
 * A visit has no status column, so "finished" is derived the way `isVisitComplete`
 * does it — no stage left `active` or `pending`.
 */
export const visitWindowEnd = (visit: VisitDetail): number | null => {
  const underway = visit.stages.some(
    (s) => s.status === "active" || s.status === "pending",
  );
  if (underway || visit.stages.length === 0) return null;
  const times = [visit.scheduledEnd, ...visit.stages.map((s) => s.completedAt)]
    .filter((at): at is string => at !== null)
    .map(Date.parse);
  return times.length > 0 ? Math.max(...times) : null;
};

/**
 * Fold the per-entity reports into one list, oldest surfacing first.
 *
 * The backend bounds a subject's surfacings by subject alone, so the patient's
 * slot returns its whole history. Bounding it to the visit window on *both* edges
 * is what keeps a per-visit verdict about this visit: a repeat patient's earlier
 * visits sit below the window, and for a finished visit their later visits sit
 * above it — grading either as part of this one accuses the wrong people. Held-out
 * rows are counted so the narrowing is disclosed rather than silently applied.
 *
 * The visit's own and its stages' slots need no bound: they cannot outlive the
 * visit.
 *
 * A null `windowEnd` is normal — an open visit has no upper edge. A null
 * `windowStart` is not: it means the lower edge could not be derived at all, so
 * the patient's whole history lands in `rows` unfiltered, and `unboundedHistory`
 * exists to make that visible instead of passing it off as a clean verdict.
 */
export const mergeAttentionRows = (
  scoped: { scope: AttentionScope; rows: ViewAttention[] }[],
  windowStart: number | null,
  windowEnd: number | null,
): AttentionReport => {
  let outsideVisit = 0;
  let patientRows = 0;
  const rows: AttentionRow[] = [];

  for (const { scope, rows: scopeRows } of scoped) {
    for (const row of scopeRows) {
      if (scope.kind === "patient") patientRows += 1;
      const at = Date.parse(row.surfacedAt);
      const outside =
        scope.kind === "patient" &&
        ((windowStart !== null && at < windowStart) ||
          (windowEnd !== null && at > windowEnd));
      if (outside) outsideVisit += 1;
      else rows.push({ ...row, scope });
    }
  }

  rows.sort((a, b) => Date.parse(a.surfacedAt) - Date.parse(b.surfacedAt));
  return {
    rows,
    outsideVisit,
    unboundedHistory: windowStart === null && patientRows > 0,
  };
};

/**
 * The surfaced-to-seen report for a whole visit — see `viewsClient.attention`
 * for the endpoint's contract and `canViewAttention` for who may read it.
 *
 * All-or-nothing rather than per-entity: a partially loaded report would read as
 * "nothing was missed", which is the one thing it must never say by accident.
 */
export const useVisitAttention = (
  visitId: string,
): AttentionReport & { visit: VisitDetail } => {
  const { data: visit } = useVisitDetail(visitId);
  const scopes = visitScopes(visit);

  const results = useSuspenseQueries({
    queries: scopes.map(({ entityId }) => ({
      queryKey: viewsKeys.attention(entityId),
      queryFn: () => viewsClient.attention(entityId),
    })),
  });

  const report = mergeAttentionRows(
    scopes.map((scope, i) => ({ scope, rows: results[i].data })),
    visitWindowStart(visit),
    visitWindowEnd(visit),
  );
  return { ...report, visit };
};
